import { useCallback, useMemo } from 'react';

import { CalendarIcon } from '@heroicons/react/24/outline';
import { PaginationState, Updater } from '@tanstack/react-table';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DataGrid } from 'src/common/DataGrid';
import { AssignmentRead } from 'src/common/external/bambi-api/bambiApi';
import { EmptyState } from 'src/common/primitives/EmptyState/EmptyState';
import { setSelectedAssignmentRows } from 'src/features/team/assignment-management/assignmentManagement.slice';

import { columns } from './columns';

export const AssignmentManagementDataGrid = ({
  assignments,
  totalCount,
  pagination,
  handlePaginationChange,
}: {
  assignments: AssignmentRead[];
  totalCount: number;
  pagination: PaginationState;
  handlePaginationChange: (paginationUpdater: Updater<PaginationState>) => void;
}) => {
  const dispatch = useAppDispatch();

  const selectedAssignmentRows = useAppSelector(
    (state) => state.assignmentManagement.selectedAssignmentRows
  );
  const selectedAssignmentRowsAsBoolean = useMemo(() => {
    const result: Record<string, boolean> = {};
    Object.keys(selectedAssignmentRows).forEach((id) => {
      result[id] = !!selectedAssignmentRows[id];
    });
    return result;
  }, [selectedAssignmentRows]);

  const setRowSelection = useCallback(
    (setter: (update: Record<string, boolean>) => Record<string, boolean>) => {
      const next: Record<string, AssignmentRead> = {};

      const result = setter(selectedAssignmentRowsAsBoolean);
      Object.keys(result).forEach((id) => {
        const value = result[id];
        const assignment = assignments.find((a) => a.id === id) ?? null;

        if (value && assignment) {
          next[id] = assignment;
        }
      });

      dispatch(setSelectedAssignmentRows(next));
    },
    [dispatch, selectedAssignmentRowsAsBoolean, assignments]
  );

  return (
    <DataGrid
      tableId={'assignment-management-table'}
      dataTestId={'assignment-management-table'}
      columns={columns}
      data={assignments}
      totalCount={totalCount}
      pagination={pagination}
      handlePaginationChange={handlePaginationChange}
      rowSelection={selectedAssignmentRowsAsBoolean}
      setRowSelection={
        setRowSelection as React.Dispatch<
          React.SetStateAction<Record<string | number | symbol, boolean>>
        >
      }
      emptyState={
        <EmptyState
          entityName="Crew Schedule"
          Title="No crew schedules found"
          Icon={<CalendarIcon className="h-6 w-6" />}
          description="Please add a crew schedule to get started or adjust your filters."
        />
      }
      getRowId={(row) => row.id}
    />
  );
};
